/**
 * RegistryRenderer — small composer that walks a `WidgetRegistry`,
 * looks up the registered component for `template`, and renders it
 * inside a `<ComponentGate>` keyed on the registration's `id`.
 *
 * Behaviour:
 *   - template not in registry → `warnUnknownTemplate(template)` and
 *     return null. No fallback widget — let the layout collapse.
 *   - template found → wrap the component in `<ComponentGate id={...}>`
 *     and pass `data` through. The gate may swallow the render if the
 *     flag is `false`; the caller doesn't need to know.
 *
 * The renderer is intentionally generic: it has no knowledge of any
 * specific widget. P5/P6 build the registry; this file just wires it
 * up to ComponentGate.
 */

import type { ReactElement } from 'react';
import { ComponentGate } from './ComponentGate.js';
import { warnUnknownTemplate, type WidgetRegistry } from './widgetRegistry.js';

interface RegistryRendererProps {
  registry: WidgetRegistry;
  template: string;
  data: unknown;
}

export function RegistryRenderer({
  registry,
  template,
  data,
}: RegistryRendererProps): ReactElement | null {
  const widget = registry.get(template);
  if (!widget) {
    warnUnknownTemplate(template);
    return null;
  }
  // The registry erases the per-widget data type to `unknown` so the
  // map can hold heterogeneous entries. We re-cast to the registered
  // component's input shape here — registrations are the trust
  // boundary for that match (P5/P6 own them).
  const Component = widget.component;
  return (
    <ComponentGate id={widget.id}>
      <Component data={data} />
    </ComponentGate>
  );
}
